/**
 * HRSundayOverridePanel — Section 6 (iter-130).
 * Lists every Site Agent — Cleaner and lets HR toggle
 * "Sunday & Holiday Commercial Override" per agent. Backend uses this
 * flag as the single source of truth for Sunday operation eligibility
 * (vehicle inspections + reservations).
 */
import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function HRSundayOverridePanel() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState('');

  const load = async () => {
    const r = await fetch(`${API}/api/procurement/vehicles/hr/cleaners`, { credentials: 'include' });
    if (r.ok) setRows(await r.json());
  };
  useEffect(() => { load(); }, []);

  const toggle = async (row, next) => {
    setBusy(row.user_id);
    const r = await fetch(`${API}/api/procurement/vehicles/hr/cleaners/${row.user_id}/sunday-override`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: next }),
    });
    setBusy('');
    if (r.ok) {
      toast.success(`${row.name} · Sunday override ${next ? 'ACTIVE' : 'INACTIVE'}`);
      setRows((rs) => rs.map((x) => x.user_id === row.user_id ? { ...x, sunday_override_active: next } : x));
    } else {
      const j = await r.json().catch(() => ({})); toast.error(j.detail || 'Toggle failed');
    }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-700 p-5" data-testid="hr-sunday-override-panel">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-5 w-5 text-[#C9A962]" />
        <h3 className="text-base font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          SUNDAY &amp; HOLIDAY OVERRIDE · SITE AGENT — CLEANERS
        </h3>
      </div>
      <p className="text-xs text-zinc-400 mb-4">
        Cleaners on a Sunday/holiday commercial contract need this on. Default is off. Only HR &amp; Admin can flip it.
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] uppercase text-zinc-500 border-b border-zinc-800">
            <th className="text-left py-2 pr-3">Cleaner</th>
            <th className="text-left py-2 pr-3">Email</th>
            <th className="text-left py-2 pr-3">Region</th>
            <th className="text-left py-2 pr-3">Status</th>
            <th className="text-right py-2 pl-3">Sunday Override</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.user_id} className="border-b border-zinc-800/60 text-zinc-200" data-testid={`sunday-row-${r.user_id}`}>
              <td className="py-3 pr-3">{r.name || '—'}</td>
              <td className="py-3 pr-3 text-zinc-500">{r.email}</td>
              <td className="py-3 pr-3 text-zinc-400">{r.region_name || r.state || '—'}</td>
              <td className="py-3 pr-3">
                {r.sunday_override_active
                  ? <Badge className="bg-emerald-700 text-white">Active</Badge>
                  : <Badge className="bg-zinc-700 text-white">Inactive</Badge>}
              </td>
              <td className="py-3 pl-3 text-right">
                <Switch checked={!!r.sunday_override_active} disabled={busy === r.user_id}
                        onCheckedChange={(v) => toggle(r, v)}
                        data-testid={`sunday-toggle-${r.user_id}`} />
              </td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={5} className="text-center text-zinc-500 py-4">No cleaners on file.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}
